import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import StripeConnectBanner from "./StripeConnectBanner";
import {
  DollarSign,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  FileText,
  RefreshCw,
  TrendingUp,
  Wallet
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

// Status badge
function StatusBadge({ status }) {
  const configs = {
    completed: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", icon: CheckCircle, label: "Completed" },
    pending: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", icon: Clock, label: "Pending" },
    approved: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", icon: CheckCircle, label: "Approved" },
    failed: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", icon: XCircle, label: "Failed" },
    pending_manual: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", icon: AlertCircle, label: "Manual" }
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded ${config.bg} ${config.border} border ${config.text}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

/**
 * PartnerPaymentHistory - Reusable payment history component for partner portals
 * 
 * Props:
 * - partnerType: "contractor" | "realtor" | "lender"
 * - apiPrefix: The API prefix (e.g., "/api/contractor")
 * - paymentTypeLabel: Custom label for payment type (e.g., "Draw Payments")
 */
export default function PartnerPaymentHistory({ 
  partnerType = "contractor", 
  apiPrefix = "/api/contractor",
  paymentTypeLabel = "Payments"
}) {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    total_received: 0,
    pending_amount: 0,
    this_month: 0,
    transaction_count: 0
  });
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}${apiPrefix}/payments/history`, {
        withCredentials: true
      });
      setPayments(response.data.payments || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error("Failed to fetch payments:", error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={`${partnerType}-payment-history`}>
      {/* Stripe Connect Banner */}
      <StripeConnectBanner 
        partnerType={partnerType} 
        apiPrefix={apiPrefix}
        onStatusChange={setStripeStatus}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ArrowDownRight className="w-5 h-5 text-emerald-400" />
            <span className="text-white/50 text-xs uppercase">Total Received</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary.total_received)}</p>
        </div>
        
        <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-amber-400" />
            <span className="text-white/50 text-xs uppercase">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{formatCurrency(summary.pending_amount)}</p>
        </div>
        
        <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white/50 text-xs uppercase">This Month</span>
          </div>
          <p className="text-2xl font-bold text-[#D4AF37]">{formatCurrency(summary.this_month)}</p>
        </div>
        
        <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            <span className="text-white/50 text-xs uppercase">Transactions</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.transaction_count}</p>
        </div>
      </div>

      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-400" />
          {paymentTypeLabel} History
        </h3>
        <Button
          onClick={fetchPayments}
          size="sm"
          className="bg-white/5 hover:bg-white/10 text-white"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Payments Table */}
      <div className="bg-[#111111] border border-white/[0.08] rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : payments.length === 0 ? (
          <div className="p-12 text-center">
            <Wallet className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <p className="text-white/50">No payments received yet</p>
            <p className="text-white/30 text-sm mt-1">
              {stripeStatus?.onboarding_complete 
                ? "Complete jobs to receive payments"
                : "Connect Stripe to start receiving payments automatically"}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left text-white/50 text-xs font-medium p-4 uppercase">Date</th>
                <th className="text-left text-white/50 text-xs font-medium p-4 uppercase">Project/Deal</th>
                <th className="text-left text-white/50 text-xs font-medium p-4 uppercase">Type</th>
                <th className="text-right text-white/50 text-xs font-medium p-4 uppercase">Amount</th>
                <th className="text-center text-white/50 text-xs font-medium p-4 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {payments.map((payment, idx) => (
                <tr key={payment.transaction_id || idx} className="hover:bg-white/[0.02]">
                  <td className="p-4">
                    <p className="text-white text-sm">{formatDate(payment.created_at)}</p>
                  </td>
                  <td className="p-4">
                    <p className="text-white text-sm">{payment.property_address || payment.deal_id || "-"}</p>
                    {payment.description && (
                      <p className="text-white/40 text-xs mt-0.5">{payment.description}</p>
                    )}
                  </td>
                  <td className="p-4">
                    <p className="text-white/70 text-sm capitalize">
                      {payment.payment_type?.replace(/_/g, ' ')}
                    </p>
                  </td>
                  <td className="p-4 text-right">
                    <p className="text-emerald-400 font-bold">{formatCurrency(payment.amount)}</p>
                  </td>
                  <td className="p-4 text-center">
                    <StatusBadge status={payment.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
